import { Add, Circle, ExpandLess, ExpandMore, FilterList, RadioButtonUnchecked } from '@mui/icons-material'
import {
  Button,
  Collapse,
  IconButton,
  Rating,
  Stack,
  TableCell,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Box } from '@mui/system'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import FilterDemandsMenu from '../../components/FilterMenu/FilterDemandsMenu'
import DemandActionMenu from '../../components/Popup/DemandActionMenu'
import DataTable from '../../components/Table/DataTable'
import AvatarComponent from '../../components/avatar/AvatarComponent'
import defaultProps from '../../config/defaultProps'
import navigation from '../../config/navigation'
import { useAppDispatch, useEditMode } from '../../store/redux/hooks'
import { getIsLeader, getIsManagement, getIsRecruiter } from '../../store/redux/reducers/currentUserSlice'
import { useGetDemandsQuery } from '../../store/redux/reducers/demandSlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { RequestProps } from '../../store/types'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../store/types/enums/SessionStorageKeys'
import { Column, Row } from '../../store/types/utils/DataTableUtils'
import usePersistedPage from '../../store/utils/usePersistedPage'
import { formatStringDate } from '../../store/utils/dateUtils'
import { StyledButtonPrimary } from '../../styles/ButtonStyles'
import { StickyCell } from '../../styles/WorklogTableStyles'

const DemandsView = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  useEffect(() => {
    dispatch(setPageName(t('page.name.demands.demands')))
    dispatch(setPageIndex(PageIndex.DEMANDS))
  }, [dispatch, t])
  const isRecruiter = useSelector(getIsRecruiter)
  const isLeader = useSelector(getIsLeader)
  const isManagement = useSelector(getIsManagement)
  const isOnlyRecruiter = isRecruiter && !isLeader && !isManagement

  const [page, setPage] = usePersistedPage(SessionStorageKeys.DemandsTablePage)
  const [requestProps, setRequestProps] = useState<RequestProps>(defaultProps)
  const { data: demandResponse, isLoading } = useGetDemandsQuery(requestProps)
  const [expandedTile, setExpandedTile] = useState<string | null>(null)

  const [isFilteringMenuOpen, setIsFilteringMenuOpen] = useState(false)
  const { setIsEditMode } = useEditMode()

  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const handleToggleCollapse = (id: string) => {
    setExpandedTile(prev => (prev === id ? null : id))
  }

  const columns: Column[] = [
    { id: 'project', label: t('page.demands.table.header.project') },
    { id: 'numberOfPeople', label: t('page.demands.table.header.numberOfPeople') },
    { id: 'technologies', label: t('page.demands.table.header.technology') },
    { id: 'description', label: t('page.demands.table.header.description') },
    { id: 'startDate', label: t('page.demands.table.header.startDate') },
    { id: 'submitter', label: t('page.demands.table.header.submitter') },
    { id: 'state', label: t('page.demands.state.label') },
    { id: 'completed', label: t('page.demands.table.header.completed') },
    { id: 'action', label: t('page.users.table.action') },
  ]

  const renderDemandTileContent = (demand: Row, _labelId: string, checkbox?: React.ReactNode) => {
    return (
      <>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box
              sx={{
                height: 'fit-content',
                border: '1px solid',
                borderRadius: 1,
                p: '5px',
                width: 'fit-content',
                borderColor: 'background.elevation8',
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {t(`page.demands.state.${demand.state.toLowerCase()}`)}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {demand.startDate ? demand.startDate.toString() : '-'}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={2}>
              {checkbox}
              <AvatarComponent
                avatar={demand.project.avatar}
                extension={demand.project.extension}
                alt={demand.project.name}
                size={40}
              />
              <Typography
                variant="h6"
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(generatePath(navigation.projectDetails, { id: demand.project.id }))}
              >
                {demand.project.name}
              </Typography>
            </Stack>
            <DemandActionMenu
              demandId={demand.id}
              isLeader={false}
              isMyDemand={false}
              isCompleted={demand.completed}
              isOnlyRecruiter={isOnlyRecruiter}
            />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body2"
              color="textSecondary"
              fontWeight={580}
              onClick={() => navigate(generatePath(navigation.userProfile, { id: demand.submitter.id }))}
            >
              {t('page.demands.table.header.submitter')}:
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate(generatePath(navigation.userProfile, { id: demand.submitter.id }))}
            >
              {`${demand.submitter.firstName} ${demand.submitter.lastName}`}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="textSecondary" fontWeight={580} paddingTop={'2px'}>
              {t('page.demands.technologies')}:
            </Typography>
            <Box>
              <Tooltip
                title={
                  <Stack spacing={1}>
                    {demand.technologies.map((tech: { name: string; level: number }) => (
                      <Stack key={tech.name} direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" color="textSecondary">
                          {tech.name}
                        </Typography>
                        <Rating
                          disabled
                          value={tech.level / 2}
                          precision={0.5}
                          icon={<Circle sx={{ width: 14, color: theme.palette.primary.main }} />}
                          emptyIcon={<RadioButtonUnchecked sx={{ width: 14, color: theme.palette.primary.main }} />}
                        />
                      </Stack>
                    ))}
                  </Stack>
                }
              >
                <Typography variant="body2" color="textSecondary">
                  {demand.technologies.map((tech: { name: string }) => tech.name).join(', ')}
                </Typography>
              </Tooltip>
            </Box>
          </Stack>
          <Stack direction="row" spacing={'5px'} alignItems="center">
            <Typography variant="body2" color="textSecondary" fontWeight={580}>
              {t('page.demands.table.header.completed')}:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {demand.completed ? t('page.demands.yes') : t('page.demands.no')}
            </Typography>
          </Stack>
          {demand.description && demand.description.length > 36 ? (
            <Stack direction="column" spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                  {!(expandedTile === demand.id) && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        cursor: 'pointer',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      {demand.description}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => handleToggleCollapse(demand.id)}>
                  {expandedTile === demand.id ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Stack>
              <Collapse in={expandedTile === demand.id} timeout="auto" unmountOnExit>
                <Typography variant="body2" color="textSecondary">
                  {demand.description}
                </Typography>
              </Collapse>
            </Stack>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {demand.description}
            </Typography>
          )}
        </Stack>
      </>
    )
  }

  const renderRow = (demand: Row) => {
    const tooltipContent = demand.technologies.map((tech: { name: string; level: number }) => (
      <Stack direction={'row'} sx={{ alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
        <Typography key={tech.name} variant="body2">
          {`${tech.name}`}
        </Typography>
        <Rating
          disabled
          name="customized-color"
          defaultValue={1}
          value={tech.level / 2}
          getLabelText={(value: number) => `${value} Heart${value !== 1 ? 's' : ''}`}
          precision={0.5}
          icon={<Circle sx={{ width: 14, mr: 0.25, color: theme.palette.primary.main }} />}
          emptyIcon={<RadioButtonUnchecked sx={{ width: 14, mr: 0.25, color: theme.palette.primary.main }} />}
          sx={{
            '&.Mui-disabled': {
              opacity: 1,
            },
          }}
        />
      </Stack>
    ))
    return (
      <>
        <TableCell>
          <Stack
            direction={'row'}
            sx={{ alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => navigate(generatePath(navigation.projectDetails, { id: demand.project.id }))}
          >
            <AvatarComponent
              extension={demand.project.extension}
              avatar={demand.project.avatar}
              alt={demand.project.name}
            />
            <Typography
              sx={{
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {demand.project.name}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>{demand.numberOfPeople}</TableCell>
        <TableCell>
          <Tooltip placement={'right'} title={tooltipContent}>
            <span>
              {demand.technologies.length === 0
                ? '-'
                : demand.technologies.map((tech: { name: string }) => tech.name).join(', ')}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell
          sx={{
            maxWidth: 250,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Tooltip placement={'bottom-start'} title={demand.description}>
            <span>{demand.description ? demand.description : '-'}</span>
          </Tooltip>
        </TableCell>
        <TableCell>{demand.startDate !== null ? formatStringDate(demand.startDate) : '-'}</TableCell>
        <TableCell>
          <Stack
            direction={'row'}
            sx={{ alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => navigate(generatePath(navigation.userProfile, { id: demand.submitter.id }))}
          >
            <AvatarComponent
              extension={demand.submitter.extension}
              avatar={demand.submitter.avatar}
              alt={demand.submitter.firstName}
            />
            <Typography
              sx={{
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >{`${demand.submitter.firstName} ${demand.submitter.lastName}`}</Typography>
          </Stack>
        </TableCell>
        <TableCell>{t(`page.demands.state.${demand.state.toLowerCase()}`)}</TableCell>
        <TableCell>{demand.completed ? t('page.demands.yes') : t('page.demands.no')}</TableCell>
        <StickyCell
          align="center"
          sx={{
            borderLeft: '2px solid',
            borderLeftColor: 'background.elevation8',
            right: 0,
            '&:hover': {
              cursor: 'auto',
            },
          }}
        >
          <DemandActionMenu
            demandId={demand.id}
            isLeader={false}
            isMyDemand={false}
            isCompleted={demand.completed}
            isOnlyRecruiter={isOnlyRecruiter}
          />
        </StickyCell>
      </>
    )
  }

  return (
    <Stack
      sx={{
        px: { xs: 0, md: 2 },
        gap: 4,
      }}
    >
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography component={'h2'} variant={'h5'}>
          {t('page.demands.allDemands')}
        </Typography>
      </Stack>
      <Stack
        direction={isDownSm ? 'column-reverse' : 'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        gap={2}
      >
        <Button
          variant="contained"
          color="secondary"
          startIcon={<FilterList />}
          className={isFilteringMenuOpen ? 'selected' : ''}
          onClick={() => setIsFilteringMenuOpen(!isFilteringMenuOpen)}
          fullWidth={isDownSm}
        >
          {t('page.users.buttons.filter')}
        </Button>
        {!isOnlyRecruiter && (
          <StyledButtonPrimary
            variant={'contained'}
            startIcon={<Add />}
            size={'medium'}
            onClick={() => {
              setIsEditMode(true)
              navigate(navigation.addDemand)
            }}
            fullWidth={isDownSm}
          >
            {t('page.demands.addDemand')}
          </StyledButtonPrimary>
        )}
      </Stack>
      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={'space-between'}
        gap={isFilteringMenuOpen ? 2 : 0}
        height="fit-content"
      >
        {isDownMd && (
          <Collapse in={isFilteringMenuOpen} orientation={'vertical'}>
            <FilterDemandsMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}

        {!isDownMd && (
          <Collapse in={isFilteringMenuOpen} orientation={'horizontal'}>
            <FilterDemandsMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}
        <DataTable
          unsortableColumns={[{ id: 'action', label: t('page.users.table.action') }]}
          renderTileContent={(demand: Row, labelId: string) =>
            renderDemandTileContent(demand, labelId) as React.ReactNode
          }
          singleSelection={false}
          columns={columns}
          data={demandResponse?.content || []}
          renderRow={renderRow}
          totalElements={demandResponse?.totalElements || 0}
          setRequestProps={setRequestProps}
          translationForNotFound={t('page.demands.table.notFound')}
          page={page}
          setPage={setPage}
          isLoading={isLoading}
          removeCheckboxes
          hoverOff
          commonRowsPerPage={
            localStorage.getItem(LocalStorageTypes.DemandsTableRows)
              ? parseInt(localStorage.getItem(LocalStorageTypes.DemandsTableRows)!)
              : undefined
          }
          localStorageRowsName={LocalStorageTypes.DemandsTableRows}
        />
      </Box>
    </Stack>
  )
}

export default DemandsView
